import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

interface NavbarProps {
  forceSolid?: boolean;
}

export default function Navbar({ forceSolid = false }: NavbarProps) {
  const { user, isAuthenticated, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forceSolid) {
      setScrolled(true);
      return;
    }
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [forceSolid]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
  };

  const showSolid = scrolled || forceSolid;

  const brandTextClass = showSolid
    ? "text-foreground-950"
    : "text-white";

  const outlineBtnClass = showSolid
    ? "border-primary-500 text-primary-600 hover:bg-primary-50 hover:border-primary-600"
    : "border-primary-400 text-white hover:bg-white/10 hover:border-primary-300";

  const mobileToggleClass = showSolid
    ? "text-foreground-800"
    : "text-white";

  const userBtnClass = showSolid
    ? "hover:bg-background-100 text-foreground-800"
    : "hover:bg-white/10 text-white";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showSolid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link to="/" className="flex items-center gap-2 no-underline">
            <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-briefcase-line text-white text-lg md:text-xl"></i>
            </div>
            <span className={`font-heading font-bold text-lg md:text-xl ${brandTextClass}`}>
              Vietnam<span className="text-primary-500">.cv</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative">
              <Link
                to="/doanh-nghiep/dang-nhap"
                className={`flex items-center gap-2 px-5 py-2.5 border rounded text-sm font-medium transition-all duration-200 no-underline cursor-pointer whitespace-nowrap ${outlineBtnClass}`}
              >
                <i className="ri-building-line text-base"></i>
                <span>Dành cho nhà tuyển dụng</span>
              </Link>
              <span className="absolute -top-1.5 -right-1.5 bg-green-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full leading-none whitespace-nowrap tracking-wide animate-pulse-badge">FREE</span>
            </div>

            {isAuthenticated && user ? (
              <>
                <Link
                  to="/tai-khoan"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 no-underline cursor-pointer whitespace-nowrap ${
                    showSolid
                      ? "hover:bg-background-100 text-foreground-700"
                      : "hover:bg-white/10 text-white"
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-3-line text-base"></i>
                  </div>
                  <span className="hidden lg:inline">Tài khoản</span>
                </Link>
                <Link
                  to="/viec-lam-da-luu"
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 no-underline cursor-pointer whitespace-nowrap ${
                    showSolid
                      ? "hover:bg-background-100 text-foreground-700"
                      : "hover:bg-white/10 text-white"
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-bookmark-line text-base"></i>
                  </div>
                  <span className="hidden lg:inline">Đã lưu</span>
                </Link>
                {user.roles.includes("admin") && (
                  <Link
                    to="/admin"
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all duration-200 no-underline cursor-pointer whitespace-nowrap ${
                      showSolid
                        ? "bg-primary-100/80 text-primary-700 hover:bg-primary-200/80"
                        : "bg-white/15 text-white hover:bg-white/25"
                    }`}
                    title="Quản trị hệ thống"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-shield-check-line text-base"></i>
                    </div>
                    <span className="hidden lg:inline">Admin</span>
                  </Link>
                )}
                <div className="relative" ref={menuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${userBtnClass}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-primary-600">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-sm font-medium max-w-[120px] truncate">
                      {user.name}
                    </span>
                    <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${
                      showSolid ? "text-foreground-500" : "text-white/70"
                    } ${userMenuOpen ? "rotate-180" : ""}`}></i>
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 top-full mt-2 w-56 bg-background-50 rounded-xl border border-background-200/70 shadow-sm overflow-hidden">
                      <div className="px-4 py-3 border-b border-background-100">
                        <p className="text-sm font-semibold text-foreground-900 truncate">{user.name}</p>
                        <p className="text-xs text-foreground-500 truncate">{user.email}</p>
                      </div>
                      <div className="py-1">
                        <Link
                          to="/tai-khoan"
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors no-underline cursor-pointer"
                        >
                          <i className="ri-user-3-line text-base text-foreground-500"></i>
                          <span>Tài khoản của tôi</span>
                        </Link>
                        <Link
                          to="/viec-lam-da-luu"
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors no-underline cursor-pointer"
                        >
                          <i className="ri-bookmark-line text-base text-foreground-500"></i>
                          <span>Việc làm đã lưu</span>
                        </Link>
                        {user.roles.includes("admin") && (
                          <Link
                            to="/admin"
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors no-underline cursor-pointer"
                          >
                            <i className="ri-shield-check-line text-base text-foreground-500"></i>
                            <span>Quản trị hệ thống</span>
                          </Link>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <i className="ri-logout-box-line text-base text-foreground-500"></i>
                          <span>Đăng xuất</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link
                to="/dang-nhap"
                className={`flex items-center gap-2 px-5 py-2.5 border rounded text-sm font-medium transition-all duration-200 no-underline cursor-pointer whitespace-nowrap ${outlineBtnClass}`}
              >
                <i className="ri-user-line text-base"></i>
                <span>Dành cho người tìm việc</span>
              </Link>
            )}
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${mobileToggleClass}`}
          >
            <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 px-6 pb-4">
          <div className="flex flex-col gap-3 pt-2">
            <div className="relative inline-flex self-center">
              <Link
                to="/doanh-nghiep/dang-nhap"
                className="flex items-center justify-center gap-2 px-5 py-2.5 border border-primary-500 rounded text-sm font-medium text-primary-600 hover:bg-primary-50 no-underline cursor-pointer whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-building-line text-base"></i>
                <span>Dành cho nhà tuyển dụng</span>
              </Link>
              <span className="absolute -top-1.5 -right-1.5 bg-green-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full leading-none whitespace-nowrap tracking-wide animate-pulse-badge">FREE</span>
            </div>

            {isAuthenticated && user ? (
              <>
                <div className="flex items-center gap-3 py-2 border-t border-background-100 mt-1 pt-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-600">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground-900 truncate">{user.name}</p>
                    <p className="text-xs text-foreground-500 truncate">{user.email}</p>
                  </div>
                </div>
                {user.roles.includes("admin") && (
                  <Link
                    to="/admin"
                    className="flex items-center gap-2 text-sm text-primary-600 font-medium hover:text-primary-700 py-2 no-underline cursor-pointer"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-shield-check-line text-base"></i>
                    </div>
                    <span>Quản trị hệ thống</span>
                  </Link>
                )}
                <button
                  onClick={() => { handleLogout(); setMobileOpen(false); }}
                  className="flex items-center gap-2 text-sm text-red-600 font-medium hover:text-red-700 py-2 cursor-pointer"
                >
                  <i className="ri-logout-box-line text-base"></i>
                  <span>Đăng xuất</span>
                </button>
                <Link
                  to="/tai-khoan"
                  className="flex items-center gap-2 text-sm text-foreground-700 font-medium hover:text-foreground-900 py-2 no-underline cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-3-line text-base"></i>
                  </div>
                  <span>Tài khoản của tôi</span>
                </Link>
                <Link
                  to="/viec-lam-da-luu"
                  className="flex items-center gap-2 text-sm text-foreground-700 font-medium hover:text-foreground-900 py-2 no-underline cursor-pointer"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-bookmark-line text-base"></i>
                  </div>
                  <span>Việc làm đã lưu</span>
                </Link>
              </>
            ) : (
              <Link
                to="/dang-nhap"
                className="flex items-center justify-center gap-2 px-5 py-2.5 bg-background-100 rounded text-sm font-medium text-foreground-800 hover:bg-background-200 no-underline cursor-pointer whitespace-nowrap transition-colors duration-200"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-user-line text-base"></i>
                <span>Dành cho người tìm việc</span>
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}